import { Switch } from '../../../../components';
import { createQuestionFromConfiguration } from '../../../logbook/views/QuestionView';
import {
    Card,
    Exercise,
    LabelBody,
    LabelDisplay1,
    LabelDisplay2,
    ProgressBar,
    SecondaryButton,
    Spacer,
    Textarea,
} from '../../components';
import { ExerciseDto, ExerciseStatus } from '../../interfaces';
import { VideoExerciseView } from '../../views';
import { normalizeInput, useSyncedState } from '../../useSyncedState';
import { ExerciseData, ExerciseViewProps } from '../../exercises';
import { Module7Exercise3Illustration1 } from '../../icons';

const page1HasSeenVideoKey = 'page1HasSeenVideo';
const page3HasSeenVideoKey = 'page3HasSeenVideo';
const page5HasSeenVideoKey = 'page5HasSeenVideo';
const page2HowWouldYouHelpAFriendKey = 'page2HowWouldYouHelpAFriend';
const page4HowWouldYouHelpYourselfKey = 'page4HowWouldYouHelpYourself';
const page6ReflectOnTheExerciseKey = 'page6ReflectOnTheExercise';

export const exerciseData33: ExerciseData = {
    ExerciseView: Module3Exercise3,
    dataKeys: [
        page2HowWouldYouHelpAFriendKey,
        page4HowWouldYouHelpYourselfKey,
        page6ReflectOnTheExerciseKey,
    ],
    questions: [
        createQuestionFromConfiguration({
            dataKeys: [page2HowWouldYouHelpAFriendKey],
            title: 'Hvordan ville du hjælpe en ven?',
            description:
                'Tænk over, hvordan du ville forsøge at være der for personen. Hvad ville du sige, og hvordan ville du hjælpe?',
        }),
        createQuestionFromConfiguration({
            dataKeys: [page4HowWouldYouHelpYourselfKey],
            title: 'Hvordan ville du hjælpe dig selv?',
            description:
                'Tænk nu over, hvordan du ville forsøge at være der for den udgave af dig, der kan have det svært. Hvad ville du sige, og hvordan ville du hjælpe?',
        }),
        createQuestionFromConfiguration({
            dataKeys: [page6ReflectOnTheExerciseKey],
            title: 'Reflekter over øvelsen',
            description:
                'Overvej nu, om der er forskel på den måde, du vil hjælpe en ven på sammenlignet med dig selv. Hvad kan du tage med dig fra øvelsen, så du bedre husker en større forståelse og omsorg for dig selv?',
        }),
    ],
};

function Module3Exercise3({ exercise, onContinue }: ExerciseViewProps) {
    const [
        page2HowWouldYouHelpAFriendInput,
        setPage2HowWouldYouHelpAFriendInput,
    ] = useSyncedState(exercise.Data[page2HowWouldYouHelpAFriendKey] ?? '');

    const [
        page4HowWouldYouHelpYourselfInput,
        setPage4HowWouldYouHelpYourselfInput,
    ] = useSyncedState(exercise.Data[page4HowWouldYouHelpYourselfKey] ?? '');

    const [page6ReflectOnTheExerciseInput, setPage6ReflectOnTheExerciseInput] =
        useSyncedState(exercise.Data[page6ReflectOnTheExerciseKey] ?? '');

    return (
        <Exercise title="Dæmp din indre kritiker">
            <ProgressBar current={exercise.CurrentPage} total={7} />
            <Switch
                value={exercise.CurrentPage}
                default={1}
                cases={{
                    1: (
                        <Page1
                            exercise={exercise}
                            page1HasSeenVideo={
                                exercise.Data[page1HasSeenVideoKey] ?? 'false'
                            }
                            onContinue={() => {
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: 2,
                                    data: {
                                        [page1HasSeenVideoKey]: 'true',
                                    },
                                });
                            }}
                        />
                    ),
                    2: (
                        <Page2
                            page2HowWouldYouHelpAFriendInput={
                                page2HowWouldYouHelpAFriendInput
                            }
                            onChangePage2HowWouldYouHelpAFriendInput={
                                setPage2HowWouldYouHelpAFriendInput
                            }
                            onContinue={() =>
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: 3,
                                    data: {
                                        [page2HowWouldYouHelpAFriendKey]:
                                            normalizeInput(
                                                page2HowWouldYouHelpAFriendInput,
                                            ),
                                    },
                                })
                            }
                        />
                    ),
                    3: (
                        <Page3
                            exercise={exercise}
                            page3HasSeenVideo={
                                exercise.Data[page3HasSeenVideoKey] ?? 'false'
                            }
                            onContinue={() => {
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: 4,
                                    data: {
                                        [page3HasSeenVideoKey]: 'true',
                                    },
                                });
                            }}
                        />
                    ),
                    4: (
                        <Page4
                            page4HowWouldYouHelpYourselfInput={
                                page4HowWouldYouHelpYourselfInput
                            }
                            onChangePage4HowWouldYouHelpYourselfInput={
                                setPage4HowWouldYouHelpYourselfInput
                            }
                            onContinue={() =>
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: 5,
                                    data: {
                                        [page4HowWouldYouHelpYourselfKey]:
                                            normalizeInput(
                                                page4HowWouldYouHelpYourselfInput,
                                            ),
                                    },
                                })
                            }
                        />
                    ),
                    5: (
                        <Page5
                            exercise={exercise}
                            page5HasSeenVideo={
                                exercise.Data[page5HasSeenVideoKey] ?? 'false'
                            }
                            onContinue={() => {
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: 6,
                                    data: {
                                        [page5HasSeenVideoKey]: 'true',
                                    },
                                });
                            }}
                        />
                    ),
                    6: (
                        <Page6
                            page6ReflectOnTheExerciseInput={
                                page6ReflectOnTheExerciseInput
                            }
                            onChangePage6ReflectOnTheExerciseInput={
                                setPage6ReflectOnTheExerciseInput
                            }
                            onContinue={() =>
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: 7,
                                    data: {
                                        [page6ReflectOnTheExerciseKey]:
                                            normalizeInput(
                                                page6ReflectOnTheExerciseInput,
                                            ),
                                    },
                                })
                            }
                        />
                    ),
                    7: (
                        <Page7
                            onContinue={() =>
                                onContinue({
                                    status: ExerciseStatus.Completed,
                                    goToNext: false,
                                })
                            }
                        />
                    ),
                }}
            />
        </Exercise>
    );
}

type Page1Props = {
    exercise: ExerciseDto;
    page1HasSeenVideo: string;
    onContinue: () => void;
};
function Page1({ exercise, page1HasSeenVideo, onContinue }: Page1Props) {
    return (
        <>
            <LabelDisplay2>Hvordan viser du omsorg?</LabelDisplay2>
            <LabelBody className="my-10">
                Det kan være svært at tale pænt til sig selv, når man er
                presset. Og ofte behandler vi os selv hårdere, end vi ville
                behandle dem vi holder af. Men tænk hvis du gav dig selv den
                samme omsorg og venlighed, som du ville give en ven. I denne
                øvelse guider vi dig til at blive klogere på, hvordan du vil
                være der for dig selv.
            </LabelBody>
            <LabelBody>Videoen guider dig igennem øvelsen.</LabelBody>
            <VideoExerciseView
                className="my-30"
                exercise={exercise}
                videoMetadata={{
                    PosterUrl:
                        'https://static.hejdoktor.dk/falcksundtsind/Falck+-+Sundt+Sind+-+Film+07+-+V05+-+Online+-+241220+-+JK.00_00_43_03.Still007.jpg',
                    VideoUrl:
                        'https://static.hejdoktor.dk/falcksundtsind/Falck+-+Sundt+Sind+-+Film+07+A+-+V05+-+Online+-+241220+-+JK.mp4',
                    MimeType: 'video/mp4',
                    Duration: '00:00:58',
                    Width: 1920,
                    Height: 1080,
                }}
                onVideoEnded={() => {
                    onContinue();
                }}
            />
            <Spacer />
            {page1HasSeenVideo === 'true' && (
                <SecondaryButton
                    onClick={() => {
                        onContinue();
                    }}
                >
                    Fortsæt øvelsen
                </SecondaryButton>
            )}
        </>
    );
}

type Page2Props = {
    page2HowWouldYouHelpAFriendInput: string;
    onChangePage2HowWouldYouHelpAFriendInput: (value: string) => void;
    onContinue: () => void;
};
function Page2({
    page2HowWouldYouHelpAFriendInput,
    onChangePage2HowWouldYouHelpAFriendInput,
    onContinue,
}: Page2Props) {
    return (
        <>
            <Card className="mb-30">
                <LabelDisplay1>Hvordan ville du hjælpe en ven?</LabelDisplay1>
                <LabelBody className="my-10">
                    Tænk over, hvordan du ville forsøge at være der for
                    personen.
                </LabelBody>
                <LabelBody>
                    Hvad ville du sige, og hvordan ville du hjælpe?
                </LabelBody>
                <Textarea
                    className="mb-10 mt-20"
                    text={page2HowWouldYouHelpAFriendInput}
                    onChangeText={onChangePage2HowWouldYouHelpAFriendInput}
                />
                <LabelBody>
                    Dit svar bliver gemt i logbogen, når du fortsætter.
                </LabelBody>
            </Card>
            <Spacer />
            <SecondaryButton onClick={onContinue}>
                Fortsæt øvelsen
            </SecondaryButton>
        </>
    );
}

type Page3Props = {
    exercise: ExerciseDto;
    page3HasSeenVideo: string;
    onContinue: () => void;
};
function Page3({ exercise, page3HasSeenVideo, onContinue }: Page3Props) {
    return (
        <div className="three-blobs-img-2 -mx-20 -mb-20 flex flex-1 flex-col">
            <Spacer />
            <LabelDisplay2 className="mx-auto">
                Fortsæt videoklippet...
            </LabelDisplay2>
            <div className="flex flex-col px-20">
                <VideoExerciseView
                    className="mb-30 mt-20"
                    exercise={exercise}
                    videoMetadata={{
                        PosterUrl:
                            'https://static.hejdoktor.dk/falcksundtsind/Falck+-+Sundt+Sind+-+Film+07+-+V05+-+Online+-+241220+-+JK.00_00_43_03.Still007.jpg',
                        VideoUrl:
                            'https://static.hejdoktor.dk/falcksundtsind/Falck+-+Sundt+Sind+-+Film+07+B+-+V05+-+Online+-+241220+-+JK.mp4',
                        MimeType: 'video/mp4',
                        Duration: '00:00:38',
                        Width: 1920,
                        Height: 1080,
                    }}
                    onVideoEnded={() => {
                        onContinue();
                    }}
                />
            </div>
            <Spacer />
            {page3HasSeenVideo === 'true' && (
                <SecondaryButton
                    className="mb-20"
                    onClick={() => {
                        onContinue();
                    }}
                >
                    Fortsæt øvelsen
                </SecondaryButton>
            )}
        </div>
    );
}

type Page4Props = {
    page4HowWouldYouHelpYourselfInput: string;
    onChangePage4HowWouldYouHelpYourselfInput: (value: string) => void;
    onContinue: () => void;
};
function Page4({
    page4HowWouldYouHelpYourselfInput,
    onChangePage4HowWouldYouHelpYourselfInput,
    onContinue,
}: Page4Props) {
    return (
        <>
            <Card className="mb-30">
                <LabelDisplay1 className="mb-10">
                    Hvordan ville du hjælpe dig selv?
                </LabelDisplay1>
                <LabelBody className="my-10">
                    Tænk nu over, hvordan du ville forsøge at være der for den
                    udgave af dig, der kan have det svært.
                </LabelBody>
                <LabelBody>
                    Hvad ville du sige, og hvordan ville du hjælpe?
                </LabelBody>
                <Textarea
                    className="mb-10 mt-20"
                    text={page4HowWouldYouHelpYourselfInput}
                    onChangeText={onChangePage4HowWouldYouHelpYourselfInput}
                />
                <LabelBody>
                    Dit svar bliver gemt i logbogen, når du fortsætter.
                </LabelBody>
            </Card>
            <Spacer />
            <SecondaryButton onClick={onContinue}>
                Fortsæt øvelsen
            </SecondaryButton>
        </>
    );
}

type Page5Props = {
    exercise: ExerciseDto;
    page5HasSeenVideo: string;
    onContinue: () => void;
};
function Page5({ exercise, page5HasSeenVideo, onContinue }: Page5Props) {
    return (
        <div className="three-blobs-img-3 -mx-20 flex flex-1 flex-col">
            <Spacer />
            <LabelDisplay2 className="mx-auto">
                Fortsæt videoklippet...
            </LabelDisplay2>
            <div className="flex flex-col px-20">
                <VideoExerciseView
                    className="mb-30 mt-20"
                    exercise={exercise}
                    videoMetadata={{
                        PosterUrl:
                            'https://static.hejdoktor.dk/falcksundtsind/Falck+-+Sundt+Sind+-+Film+07+-+V05+-+Online+-+241220+-+JK.00_00_43_03.Still007.jpg',
                        VideoUrl:
                            'https://static.hejdoktor.dk/falcksundtsind/Falck+-+Sundt+Sind+-+Film+07+C+-+V05+-+Online+-+241220+-+JK.mp4',
                        MimeType: 'video/mp4',
                        Duration: '00:00:36',
                        Width: 1920,
                        Height: 1080,
                    }}
                    onVideoEnded={() => {
                        onContinue();
                    }}
                />
            </div>
            <Spacer />
            {page5HasSeenVideo === 'true' && (
                <SecondaryButton
                    onClick={() => {
                        onContinue();
                    }}
                >
                    Fortsæt øvelsen
                </SecondaryButton>
            )}
        </div>
    );
}

type Page6Props = {
    page6ReflectOnTheExerciseInput: string;
    onChangePage6ReflectOnTheExerciseInput: (value: string) => void;
    onContinue: () => void;
};
function Page6({
    page6ReflectOnTheExerciseInput,
    onChangePage6ReflectOnTheExerciseInput,
    onContinue,
}: Page6Props) {
    return (
        <>
            <Card className="mb-30">
                <LabelDisplay1>Reflekter over øvelsen</LabelDisplay1>
                <LabelBody className="my-10">
                    Overvej nu, om der er forskel på den måde, du vil hjælpe en
                    ven på sammenlignet med dig selv.
                </LabelBody>
                <LabelBody>
                    Hvad kan du tage med dig fra øvelsen, så du bedre husker en
                    større forståelse og omsorg for dig selv, når du er presset?
                </LabelBody>
                <Textarea
                    className="mb-10 mt-20"
                    text={page6ReflectOnTheExerciseInput}
                    onChangeText={onChangePage6ReflectOnTheExerciseInput}
                />
                <LabelBody>
                    Dit svar bliver gemt i logbogen, når du fortsætter.
                </LabelBody>
            </Card>
            <Spacer />
            <SecondaryButton onClick={onContinue}>Fortsæt</SecondaryButton>
        </>
    );
}

type Page7Props = {
    onContinue: () => void;
};
function Page7({ onContinue }: Page7Props) {
    return (
        <>
            <LabelDisplay2 className="mb-10">Afrunding</LabelDisplay2>
            <LabelBody>
                Husk at du er ikke alene, alle kan have det svært. Du er bare et
                menneske – hverken mindre eller mere. Næste gang du f.eks.
                oplever at være opslugt af selvkritik, følelser af
                utilstrækkelighed eller tanker om at du skal kunne klare mere,
                så prøv de ting af, du har skrevet ned. Det kan have en
                beroligende effekt.
            </LabelBody>
            <Module7Exercise3Illustration1 className="mx-auto my-30" />
            <Spacer />
            <SecondaryButton onClick={onContinue}>
                Afslut øvelsen
            </SecondaryButton>
        </>
    );
}
